import React, { useState, useEffect, useRef, useCallback } from 'react';
import PriceChart from './PriceChart';
import { fetchNews, fetchFundamentals } from '../services/priceService';
import { PROXY_BASE } from '../config/apiConfig';
import { cleanTicker, logoTicker, fmtPrice, fmt } from '../utils/format';
import { TICKER_FALLBACK } from '../config/constants';
import { getAuthHeaders } from '../services/authService';
import { trackSignalView } from '../utils/reviewPrompter';
import './AssetDetail.css';

function DetailAvatar({ ticker }) {
  const [imgFailed, setImgFailed] = useState(false);
  const sym      = cleanTicker(ticker);
  const logoUrl  = `https://assets.parqet.com/logos/symbol/${logoTicker(ticker)}`;
  const fallback = TICKER_FALLBACK[ticker] || { bg: '#2563eb', color: '#fff' };

  if (!imgFailed) {
    return (
      <div className="detail-avatar">
        <img
          src={logoUrl}
          alt={sym}
          onError={() => setImgFailed(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </div>
    );
  }
  return (
    <div className="detail-avatar detail-avatar--fallback"
      style={{ background: fallback.bg, color: fallback.color }}>
      {sym.slice(0, 2)}
    </div>
  );
}

const SIGNAL_MAP  = { 'Kaufen': 'buy', 'Verkaufen': 'sell', 'Halten': 'hold' };
const BADGE_LABEL = { buy: 'BUY', sell: 'SELL', hold: 'HOLD' };

const ANALYST_LABEL = {
  strong_buy:   { text: 'Strong Buy',       color: '#22c55e' },
  buy:          { text: 'Buy',              color: '#22c55e' },
  hold:         { text: 'Hold',             color: '#94a3b8' },
  underperform: { text: 'Underperform',     color: '#f97316' },
  sell:         { text: 'Sell',             color: '#ef4444' },
  strong_sell:  { text: 'Strong Sell',      color: '#ef4444' },
};

function fmtB(v) {
  if (v == null || isNaN(v)) return '—';
  const abs = Math.abs(v);
  if (abs >= 1e9) return `${(v / 1e9).toFixed(1)} Mrd`;
  if (abs >= 1e6) return `${(v / 1e6).toFixed(0)} Mio`;
  if (abs >= 1e3) return `${(v / 1e3).toFixed(0)}K`;
  return `${v}`;
}

function relTime(unixTs) {
  if (!unixTs) return '';
  const diff = Math.floor(Date.now() / 1000) - unixTs;
  if (diff < 3600)  return `vor ${Math.floor(diff / 60)} Min.`;
  if (diff < 86400) return `vor ${Math.floor(diff / 3600)} Std.`;
  return `vor ${Math.floor(diff / 86400)} Tagen`;
}

function isAlreadyInPortfolio(ticker) {
  try {
    return JSON.parse(localStorage.getItem('finanzapp_portfolio_v1') || '[]')
      .some(e => e.ticker === ticker);
  } catch { return false; }
}

function WtLogo({ ticker }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return <div className="wt-insider-logo wt-insider-logo--fallback">{ticker.slice(0, 4)}</div>;
  }
  return (
    <div className="wt-insider-logo">
      <img src={`https://assets.parqet.com/logos/symbol/${ticker}?format=svg`} alt={ticker}
        onError={() => setFailed(true)} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
    </div>
  );
}

function AssetDetail({ asset, onClose, onAddToPortfolio }) {
  const reboundActive = (() => {
    try { return localStorage.getItem('bullivo_rebound_mode') === '1'; } catch { return false; }
  })();
  const cls = SIGNAL_MAP[asset.signal] || 'hold';
  const c24 = asset.change  ?? 0;
  const c7d = asset.change7d ?? 0;

  const [added,         setAdded]         = useState(() => isAlreadyInPortfolio(asset.ticker));
  const [adding,        setAdding]        = useState(false);
  const [news,          setNews]          = useState([]);
  const [hoverPrice,    setHoverPrice]    = useState(null);
  const [hoverChange,   setHoverChange]   = useState(null);
  const [chartPeriod,   setChartPeriod]   = useState('1M');
  const [fundamentals,  setFundamentals]  = useState(null);
  const [periodAnnual, setPeriodAnnual] = useState(false);
  const [whaleTrades,   setWhaleTrades]   = useState([]);
  const [fundMoves,     setFundMoves]     = useState([]); // Top-Fonds 13F für diesen Ticker
  const [fundamentalsError, setFundamentalsError] = useState(false);
  const [whaleError,    setWhaleError]    = useState(false);
  const [fundError,     setFundError]     = useState(false);

  // ── Body-Scroll sperren wenn Sheet offen ──
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // ── Review-Prompter: Signal-View tracken (3x → SKStoreReviewController) ──
  useEffect(() => {
    if (asset?.ticker) trackSignalView();
  }, [asset?.ticker]);

  // ── Swipe-to-dismiss ──
  const sheetRef    = useRef(null);
  const dragY       = useRef(0);
  const dragX       = useRef(0);
  const startY      = useRef(0);
  const startX      = useRef(0);
  const dragging    = useRef(false);  // vertical
  const draggingX   = useRef(false);  // horizontal (left-edge back)
  const intentSet   = useRef(false);

  const onTouchStart = useCallback((e) => {
    startY.current   = e.touches[0].clientY;
    startX.current   = e.touches[0].clientX;
    dragY.current    = 0;
    dragX.current    = 0;
    dragging.current  = false;
    draggingX.current = false;
    intentSet.current = false;
  }, []);

  const onTouchMove = useCallback((e) => {
    const sheet = sheetRef.current;
    if (!sheet) return;
    const dy = e.touches[0].clientY - startY.current;
    const dx = e.touches[0].clientX - startX.current;

    if (!intentSet.current) {
      intentSet.current = true;
      // Left-edge swipe: starts within 30px of left edge, moves right
      if (startX.current < 30 && dx > 6) {
        draggingX.current = true;
      } else if (sheet.scrollTop === 0 && dy > 6) {
        dragging.current = true;
      } else {
        return;
      }
    }

    if (draggingX.current) {
      if (dx <= 0) return;
      dragX.current = dx;
      sheet.style.transform = `translateX(${dx}px)`;
      sheet.style.transition = 'none';
      return;
    }

    if (!dragging.current) return;
    if (dy <= 0) return;
    dragY.current = dy;
    sheet.style.transform = `translateY(${dy}px)`;
    sheet.style.transition = 'none';
  }, []);

  const onTouchEnd = useCallback(() => {
    const sheet = sheetRef.current;

    if (draggingX.current) {
      draggingX.current = false;
      intentSet.current = false;
      if (dragX.current > 80) {
        if (sheet) {
          sheet.style.transition = 'transform 0.25s ease-out';
          sheet.style.transform = 'translateX(100%)';
        }
        setTimeout(onClose, 200);
      } else {
        if (sheet) {
          sheet.style.transition = 'transform 0.2s ease-out';
          sheet.style.transform = 'translateX(0)';
        }
      }
      return;
    }

    if (!dragging.current) return;
    dragging.current = false;
    intentSet.current = false;
    if (dragY.current > 120) {
      if (sheet) {
        sheet.style.transition = 'transform 0.25s ease-out';
        sheet.style.transform = 'translateY(100%)';
      }
      setTimeout(onClose, 200);
    } else {
      if (sheet) {
        sheet.style.transition = 'transform 0.2s ease-out';
        sheet.style.transform = 'translateY(0)';
      }
    }
  }, [onClose]);

  // Nachrichten laden (nur für Sentiment-Analyse, nicht angezeigt)
  useEffect(() => {
    fetchNews(asset.ticker).then(d => setNews(Array.isArray(d) ? d : [])).catch(() => {});
  }, [asset.ticker]);

  // Fundamentaldaten laden
  useEffect(() => {
    fetchFundamentals(asset.ticker).then(d => setFundamentals(d)).catch(() => setFundamentalsError(true));
  }, [asset.ticker]);

  // Whale Trades laden (serverseitig nach Ticker gefiltert)
  useEffect(() => {
    const sym = cleanTicker(asset.ticker);
    fetch(`${PROXY_BASE}/api/whale-trades?ticker=${encodeURIComponent(sym)}`, { headers: { Accept: 'application/json' } })
      .then(r => r.ok ? r.json() : [])
      .then(data => setWhaleTrades(Array.isArray(data) ? data : []))
      .catch(() => setWhaleError(true));
  }, [asset.ticker]);

  // Top-Fonds 13F laden (gecacht am Server, schnell)
  useEffect(() => {
    const sym = cleanTicker(asset.ticker);
    getAuthHeaders().then(h => fetch(`${PROXY_BASE}/api/big-money`, { headers: { Accept: 'application/json', ...h } }))
      .then(r => r.ok ? r.json() : {})
      .then(data => {
        const moves = (data.investorMoves || []).flatMap(inv =>
          inv.changes
            .filter(ch => ch.ticker === sym)
            .map(ch => ({ investor: inv.investor, fund: inv.fund, quarter: inv.quarter, ...ch }))
        );
        setFundMoves(moves);
      })
      .catch(() => setFundError(true));
  }, [asset.ticker]);

  const handleAddToPortfolio = async () => {
    if (added || adding || !onAddToPortfolio) return;
    setAdding(true);
    await onAddToPortfolio(asset);
    setAdded(true);
    setAdding(false);
  };

  // Analysten-Infos
  const analystInfo   = asset.recommendationKey ? ANALYST_LABEL[asset.recommendationKey] : null;
  const analystCount  = asset.numberOfAnalystOpinions;
  const targetPrice   = asset.targetMeanPrice;
  const upsidePct     = targetPrice && asset.price
    ? ((targetPrice - asset.price) / asset.price * 100)
    : null;

  return (
    <div className="detail-overlay" onClick={onClose}>
      <div
        className={`detail-sheet${reboundActive ? ' detail-sheet--rebound' : ''}`}
        ref={sheetRef}
        onClick={e => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div className="detail-handle" />

        {/* ── Falling-Knife-Warnung ── */}
        {asset.fallingKnife && (
          <div className="detail-knife-banner">
            <span className="detail-knife-icon">⚠️</span>
            <div>
              <p className="detail-knife-title">Möglicher Falling Knife</p>
              <p className="detail-knife-sub">
                Starker Kursrückgang bei negativem Analysten-Konsens. Fundamentale Lage prüfen.
              </p>
            </div>
          </div>
        )}

        {/* ── Top card: hero + chart combined ── */}
        <div className="detail-top-card">
        <div className="detail-hero">
          <div className="detail-header">
            <DetailAvatar ticker={asset.ticker} />
            <div className="detail-identity">
              <span className="detail-ticker">{asset.ticker.replace('.DE','').replace('.PA','').replace('.SW','').replace('.AS','')}</span>
              <span className="detail-name">{asset.name}</span>
              {asset.sector && (
                <span className="detail-sector">{asset.sector}{asset.industry ? ` · ${asset.industry}` : ''}</span>
              )}
            </div>
            <button className="detail-close" onClick={onClose}>✕</button>
          </div>

          <div className="detail-price-row">
            <span className="detail-price">{fmtPrice(hoverPrice ?? asset.price)}</span>
            {onAddToPortfolio && (
              <button
                className={`detail-badge-btn ${added ? 'detail-badge-btn--added' : ''}`}
                onClick={handleAddToPortfolio}
                disabled={added || adding}
              >
                {adding ? '…' : added ? '✓ Portfolio' : '+ Portfolio'}
              </button>
            )}
          </div>

          {added && (
            <p className="detail-added-hint">Zur Watchlist hinzugefügt</p>
          )}

          <div className="detail-changes">
            <span className={`detail-signal-inline ${cls}`}>{BADGE_LABEL[cls]}</span>
            <span className="detail-sep">·</span>
            <span className={c24 >= 0 ? 'pos' : 'neg'}>{fmt(c24)} heute</span>
            <span className="detail-sep">·</span>
            {hoverChange != null
              ? <span className={hoverChange >= 0 ? 'pos' : 'neg'}>{hoverChange >= 0 ? '+' : ''}{hoverChange.toFixed(2)}% {chartPeriod}</span>
              : <span className={c7d >= 0 ? 'pos' : 'neg'}>{fmt(c7d)} 7T</span>
            }
            <span className="detail-sep">·</span>
            <span className="detail-conf">Score {asset.confidence ?? '—'}%</span>
          </div>
        </div>

        {/* ── Chart ── */}
        <div className="detail-chart-wrapper">
          <PriceChart ticker={asset.ticker} change7d={c7d} onHoverPrice={setHoverPrice} onHoverChange={setHoverChange} onPeriodChange={setChartPeriod} rebound={reboundActive} />
        </div>
        </div>

        {/* ── Warum diese Empfehlung ── */}
        <div className="detail-section">
          <h3 className="detail-section-title">Warum {BADGE_LABEL[cls]}?</h3>
          <div className="detail-reasons-card">
            <ul className="detail-reasons">
              {(asset.reasons ?? []).map((r, i) => (
                <li key={i} className="detail-reason-item">
                  <span className={`detail-reason-dot ${cls}`} />
                  {r}
                </li>
              ))}
              {fundamentals && (() => {
                const { trends } = fundamentals;
                if (!fundamentals.quarters) return null;
                const revVals = fundamentals.quarters.map(q => q.revenue).filter(v => v != null);
                const revDown = trends.revenue < 0;
                const revUp   = trends.revenue > 0;
                const revGrowth = revVals.length >= 2 ? ((revVals[revVals.length-1] - revVals[0]) / Math.abs(revVals[0]) * 100) : null;
                const growthStr = revGrowth != null ? `${revGrowth >= 0 ? '+' : ''}${revGrowth.toFixed(0)}%` : null;
                const text = growthStr ? `Umsatz ${growthStr}` : revUp ? `Umsatz wächst` : revDown ? `Umsatz rückläufig` : `Umsatz stabil`;
                const dotCls = revDown ? 'warn' : revUp ? cls : 'neutral';
                return (
                  <li className="detail-reason-item">
                    <span className={`detail-reason-dot ${dotCls}`} />
                    {text}
                  </li>
                );
              })()}
            </ul>
          </div>
        </div>

        {/* ── Termine ── */}
        {fundamentals?.events?.length > 0 && (() => {
          const now = Date.now();
          const events = fundamentals.events.filter(e => e.ts >= now && e.type === 'earnings').slice(0, 1);
          if (!events.length) return null;
          const months = ['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'];
          const fmtFullDate = (ts) => {
            const d = new Date(ts);
            return `${d.getDate()}. ${months[d.getMonth()]} ${d.getFullYear()}`;
          };
          const fmtCountdown = (ts) => {
            const t = new Date(ts);
            const today = new Date();
            const targetDay = new Date(t.getFullYear(), t.getMonth(), t.getDate());
            const todayDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
            const days = Math.round((targetDay - todayDay) / 86400000);
            if (days === 0) return 'heute';
            if (days === 1) return 'morgen';
            if (days === -1) return 'gestern';
            if (days > 0 && days < 31) return `in ${days} Tagen`;
            if (days < 0 && days > -31) return `vor ${-days} Tagen`;
            const w = Math.round(Math.abs(days) / 7);
            return days > 0 ? `in ${w} Wochen` : `vor ${w} Wochen`;
          };
          const typeLabel = (t) => {
            if (t === 'earnings') return 'Quartalszahlen';
            if (t === 'dividend') return 'Dividende';
            if (t === 'exDividend') return 'Ex-Dividende';
            return 'Termin';
          };
          const sentimentFor = (ev) => {
            if (ev.type !== 'earnings') return null;
            const eg = ev.epsGrowth;
            const rg = ev.revenueGrowth;
            if (eg == null && rg == null) return null;
            const eps = eg ?? rg;
            const rev = rg ?? eg;
            let score = eps * 0.6 + rev * 0.4;
            const up = ev.revisionsUp || 0;
            const down = ev.revisionsDown || 0;
            const total = up + down;
            if (total >= 5) {
              const bias = (up - down) / total;
              if (bias > 0.5) score += 0.08;
              else if (bias < -0.5) score -= 0.08;
            }
            // 5-Segment-Indikator: -2..+2 mapping
            if (score >= 0.40) return { label: 'Sehr stark', tone: 'pos-strong', fill: 5 };
            if (score >= 0.15) return { label: 'Stark positiv', tone: 'pos-strong', fill: 4 };
            if (score >= 0.05) return { label: 'Positiv', tone: 'pos', fill: 4 };
            if (score >= -0.05) return { label: 'Stabil', tone: 'neutral', fill: 3 };
            if (score >= -0.15) return { label: 'Verhalten', tone: 'warn', fill: 2 };
            if (score >= -0.30) return { label: 'Schwach', tone: 'neg', fill: 2 };
            return { label: 'Sehr schwach', tone: 'neg-strong', fill: 1 };
          };
          return (
            <div className="detail-section">
              <div className="detail-events-header">
                <h3 className="detail-section-title">Termine</h3>
                {events.length > 1 && <span className="detail-events-count">{events.length}</span>}
              </div>
              <div className="detail-events-card">
                {events.map((ev, i) => {
                  const isPast = ev.ts < now;
                  const sentiment = sentimentFor(ev);
                  return (
                    <div key={i} className={`detail-event ${ev.type} ${isPast ? 'is-past' : ''} ${i === 0 ? 'is-first' : ''} ${i === events.length - 1 ? 'is-last' : ''}`}>
                      <div className="detail-event-head">
                        <span className="detail-event-type">
                          {typeLabel(ev.type)}
                          {ev.estimated && <span className="detail-event-est"> · Schätzung</span>}
                        </span>
                        <span className="detail-event-when">{fmtCountdown(ev.ts)}</span>
                      </div>
                      <div className="detail-event-date">{fmtFullDate(ev.ts)}</div>
                      {sentiment && (
                        <div className="detail-event-sentiment">
                          <div className={`detail-event-gauge tone-${sentiment.tone}`}>
                            {[1,2,3,4,5].map(n => (
                              <span key={n} className={`detail-event-gauge-seg ${n <= sentiment.fill ? 'is-on' : ''}`} />
                            ))}
                          </div>
                          <span className="detail-event-sentiment-text">
                            <span className="detail-event-sentiment-label">Erwartungen</span>
                            <span className={`detail-event-sentiment-value tone-${sentiment.tone}`}>{sentiment.label}</span>
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* ── Stats ── */}
        <div className="detail-section">
          <h3 className="detail-section-title">Kennzahlen</h3>
          {!fundamentals && fundamentalsError && (
            <p className="detail-fetch-error">Fundamentaldaten nicht verfügbar</p>
          )}
          <div className="detail-stats">
            {/* Row 1: Bewertung | Risiko */}
            {(asset.trailingPE != null || asset.forwardPE != null || (asset.priceToCashflow != null && asset.priceToCashflow > 0)) && (() => {
              const peTrailing = asset.trailingPE;
              const peForward = asset.forwardPE;
              // Trailing-PE primaer; bei SaaS-Verlust (null) oder verzerrt-hoch (>100) auf Forward-PE ausweichen
              const peUsed = (peTrailing != null && peTrailing > 0 && peTrailing <= 100) ? peTrailing : peForward;
              const peIsForward = peUsed === peForward && peTrailing !== peUsed;
              const cf = asset.priceToCashflow;
              let peScore = 0, cfScore = 0;
              if (peUsed != null && peUsed > 0) {
                if (peUsed < 12) peScore = 2;
                else if (peUsed < 18) peScore = 1;
                else if (peUsed > 50) peScore = -2;
                else if (peUsed > 35) peScore = -1;
              }
              if (cf != null && cf > 0) {
                if (cf < 10) cfScore = 2;
                else if (cf < 18) cfScore = 1;
                else if (cf > 40) cfScore = -1;
              }
              const combined = peScore + cfScore;
              let label, cls;
              if (combined >= 3)       { label = 'Günstig'; cls = 'pos'; }
              else if (combined >= 1)  { label = 'Fair'; cls = ''; }
              else if (combined >= -1) { label = 'Leicht teuer'; cls = ''; }
              else                     { label = 'Teuer'; cls = 'neg'; }
              const parts = [];
              if (peUsed != null && peUsed > 0) parts.push(`KGV ${peUsed.toFixed(0)}`);
              if (cf != null && cf > 0) parts.push(`KCV ${cf.toFixed(0)}`);
              return (
                <div className="detail-stat">
                  <span className="ds-label">Bewertung</span>
                  <span className={`ds-value ${cls}`}>{label}</span>
                  <span className="ds-hint">{parts.join(' · ')}</span>
                </div>
              );
            })()}
            <div className="detail-stat">
              <span className="ds-label">Risiko</span>
              <span className="ds-value">{asset.risk ?? '—'}</span>
            </div>

            {/* Row 3+: Kurs-Analytik */}
            <div className="detail-stat">
              <span className="ds-label">24h</span>
              <span className={`ds-value ${c24 >= 0 ? 'pos' : 'neg'}`}>{fmt(c24)}</span>
            </div>
            <div className="detail-stat">
              <span className="ds-label">7 Tage</span>
              <span className={`ds-value ${c7d >= 0 ? 'pos' : 'neg'}`}>{fmt(c7d)}</span>
            </div>
            <div className="detail-stat">
              <span className="ds-label">RSI (14)</span>
              <span className={`ds-value ${asset.rsi != null ? (asset.rsi < 30 ? 'pos' : asset.rsi > 70 ? 'neg' : '') : ''}`}>
                {asset.rsi != null ? Number(asset.rsi).toFixed(1) : '—'}
              </span>
            </div>
            <div className="detail-stat">
              <span className="ds-label">52W-Position</span>
              <span className="ds-value">{asset.week52Position != null ? `${(asset.week52Position <= 1.5 ? asset.week52Position * 100 : asset.week52Position).toFixed(1)}%` : '—'}</span>
            </div>
            <div className="detail-stat">
              <span className="ds-label">Score</span>
              <span className="ds-value">{asset.confidence ?? '—'}%</span>
            </div>
          </div>
        </div>


        {/* ── Finanzen (TR-Style) ── */}
        {fundamentals?.quarters && (() => {
          const qs = fundamentals.quarters.filter(q => q.revenue != null && q.date);
          if (!qs.length) return null;
          const sum = arr => arr.reduce((a, b) => a + (b ?? 0), 0);
          const lastQ = qs[qs.length - 1];
          const prevQ = qs[qs.length - 5];
          const annual = fundamentals.annual || [];
          let yLast = null, yPrev = null, yRev = null, yOp = null, yNI = null, yRevPrev = null, yOpPrev = null, yNIPrev = null;
          if (annual.length >= 1) {
            const annComplete = annual.filter(a => a.revenue != null);
            const last = annComplete[annComplete.length - 1];
            const prev = annComplete[annComplete.length - 2];
            yLast = last?.year; yPrev = prev?.year;
            yRev = last?.revenue ?? null; yOp = last?.operatingIncome ?? null; yNI = last?.netIncome ?? null;
            yRevPrev = prev?.revenue ?? null; yOpPrev = prev?.operatingIncome ?? null; yNIPrev = prev?.netIncome ?? null;
          } else {
            const byYear = {};
            qs.forEach(q => {
              const y = (q.date || '').slice(0, 4);
              if (!y) return;
              if (!byYear[y]) byYear[y] = [];
              byYear[y].push(q);
            });
            const fullYears = Object.keys(byYear).sort().filter(y => byYear[y].length === 4);
            yLast = fullYears[fullYears.length - 1];
            yPrev = fullYears[fullYears.length - 2];
            const sy = (y, k) => y ? sum(byYear[y].map(q => q[k])) : null;
            yRev = sy(yLast, 'revenue'); yOp = sy(yLast, 'operatingIncome'); yNI = sy(yLast, 'netIncome');
            yRevPrev = sy(yPrev, 'revenue'); yOpPrev = sy(yPrev, 'operatingIncome'); yNIPrev = sy(yPrev, 'netIncome');
          }
          // GAAP vs Non-GAAP: bei gaap-divergent Aktien (SaaS/SBC-heavy) automatisch Non-GAAP-Ansicht (matched TR).
          const isDivergent = fundamentals.accountingMode === 'gaap-divergent';
          const useNonGaap = isDivergent && lastQ.netIncomeNonGaap != null;
          const lastNiNonGaap = lastQ.netIncomeNonGaap;
          const prevNiNonGaap = prevQ?.netIncomeNonGaap;
          // Quartal vs Annual
          const qRev = lastQ.revenue, qOp = lastQ.operatingIncome;
          const qNiGaap = lastQ.netIncome;
          const qNi = useNonGaap && lastNiNonGaap != null ? lastNiNonGaap : qNiGaap;
          const qRevGrowth = (prevQ?.revenue) ? ((qRev - prevQ.revenue) / Math.abs(prevQ.revenue) * 100) : null;
          const qOpGrowth = (prevQ?.operatingIncome != null && prevQ.operatingIncome !== 0) ? ((qOp - prevQ.operatingIncome) / Math.abs(prevQ.operatingIncome) * 100) : null;
          // STRICT: nie GAAP/NonGAAP mischen — Growth nur wenn beide Werte gleicher Methode vorliegen
          const qNiPrevForGrowth = useNonGaap ? prevNiNonGaap : prevQ?.netIncome;
          const qNiCurForGrowth = useNonGaap ? lastNiNonGaap : qNiGaap;
          const qNiGrowth = (qNiPrevForGrowth != null && qNiPrevForGrowth !== 0 && qNiCurForGrowth != null) ? ((qNiCurForGrowth - qNiPrevForGrowth) / Math.abs(qNiPrevForGrowth) * 100) : null;
          // Annual Non-GAAP: summe der 4 Q nonGaap, wenn verfügbar
          let yNINonGaap = null, yNIPrevNonGaap = null;
          if (useNonGaap) {
            const byYearNG = {};
            qs.forEach(q => {
              const y = (q.date || '').slice(0, 4);
              if (!y || q.netIncomeNonGaap == null) return;
              if (!byYearNG[y]) byYearNG[y] = [];
              byYearNG[y].push(q.netIncomeNonGaap);
            });
            const yrs = Object.keys(byYearNG).sort();
            const lastY = yrs[yrs.length - 1], prevY = yrs[yrs.length - 2];
            if (lastY && byYearNG[lastY].length === 4) yNINonGaap = sum(byYearNG[lastY]);
            if (prevY && byYearNG[prevY].length === 4) yNIPrevNonGaap = sum(byYearNG[prevY]);
          }
          const aRevGrowth = (yRev != null && yRevPrev) ? ((yRev - yRevPrev) / Math.abs(yRevPrev) * 100) : null;
          const aOpGrowth = (yOp != null && yOpPrev) ? ((yOp - yOpPrev) / Math.abs(yOpPrev) * 100) : null;
          const yNIDisp = useNonGaap && yNINonGaap != null ? yNINonGaap : yNI;
          const yNIPrevDisp = useNonGaap && yNIPrevNonGaap != null ? yNIPrevNonGaap : yNIPrev;
          const aNiGrowth = (yNIDisp != null && yNIPrevDisp) ? ((yNIDisp - yNIPrevDisp) / Math.abs(yNIPrevDisp) * 100) : null;
          const revAbs = periodAnnual ? yRev : qRev;
          const ebitAbs = periodAnnual ? yOp : qOp;
          const niAbs = periodAnnual ? yNIDisp : qNi;
          const revGrowth = periodAnnual ? aRevGrowth : qRevGrowth;
          const ebitGrowth = periodAnnual ? aOpGrowth : qOpGrowth;
          const niGrowth = periodAnnual ? aNiGrowth : qNiGrowth;
          const epsActual = lastQ.epsActualNonGaap;
          // Currency
          const currencySuffix = (() => {
            const t = asset.ticker || '';
            if (/\.(DE|PA|AS|SW|MI|VI|BR|HE|ST|OL|CO)$/i.test(t)) return '€';
            if (/\.L$/i.test(t)) return '£';
            if (/\.HK$/i.test(t)) return 'HK$';
            if (/\.TO$/i.test(t)) return 'CA$';
            return '$';
          })();
          const fmtMio = n => {
            if (n == null) return '—';
            const abs = Math.abs(n);
            let s;
            if (abs >= 1e9) s = `${(n / 1e9).toFixed(1).replace('.', ',')} Mrd`;
            else if (abs >= 1e6) s = `${Math.round(n / 1e6).toLocaleString('de-DE')} Mio`;
            else if (abs >= 1e3) s = `${(n / 1e3).toFixed(1).replace('.', ',')} Tsd`;
            else s = `${Math.round(n).toLocaleString('de-DE')}`;
            return `${s} ${currencySuffix}`;
          };
          const fmtPct = v => v != null ? `${v >= 0 ? '+' : ''}${v.toFixed(2).replace('.', ',')} %` : '—';
          const periodTag = periodAnnual ? (yLast || 'Jahr') : (() => {
            const d = lastQ.date || '';
            const m = parseInt(d.slice(5, 7), 10);
            const y = d.slice(2, 4);
            const qNum = m ? Math.ceil(m / 3) : null;
            return qNum ? `Q${qNum}/${y}` : 'Quartal';
          })();
          return (
            <div className="detail-section">
              <div className="finanzen-header">
                <h3 className="detail-section-title finanzen-title">Finanzen</h3>
                <button className="finanzen-toggle" onClick={() => setPeriodAnnual(a => !a)}>
                  {periodAnnual ? 'Jährlich' : 'Quartal'} · {periodTag}
                  <span className="finanzen-toggle-chev">▾</span>
                </button>
              </div>
              <div className="finanzen-rows">
                <div className="finanzen-row">
                  <span className="finanzen-label">Umsatz</span>
                  <span className="finanzen-value">{fmtMio(revAbs)}</span>
                  <span className={`finanzen-growth ${revGrowth != null ? (revGrowth >= 0 ? 'pos' : 'neg') : ''}`}>
                    {revGrowth != null ? `${revGrowth >= 0 ? '▲' : '▼'} ${fmtPct(revGrowth)}` : '—'}
                  </span>
                </div>
                {!useNonGaap && (
                  <div className="finanzen-row">
                    <span className="finanzen-label">EBIT</span>
                    <span className="finanzen-value">{fmtMio(ebitAbs)}</span>
                    <span className={`finanzen-growth ${ebitGrowth != null ? (ebitGrowth >= 0 ? 'pos' : 'neg') : ''}`}>
                      {ebitGrowth != null ? `${ebitGrowth >= 0 ? '▲' : '▼'} ${fmtPct(ebitGrowth)}` : '—'}
                    </span>
                  </div>
                )}
                <div className="finanzen-row">
                  <span className="finanzen-label">Nettogewinn</span>
                  <span className="finanzen-value">{fmtMio(niAbs)}</span>
                  <span className={`finanzen-growth ${niGrowth != null ? (niGrowth >= 0 ? 'pos' : 'neg') : ''}`}>
                    {niGrowth != null ? `${niGrowth >= 0 ? '▲' : '▼'} ${fmtPct(niGrowth)}` : '—'}
                  </span>
                </div>
              </div>
              {useNonGaap && (
                <p className="finanzen-disclaimer">Bereinigte Zahlen (Non-GAAP) — Aktien-basierte Vergütung herausgerechnet, wie in Earnings-Reports & TR.</p>
              )}
            </div>
          );
        })()}

        {/* ── Whale Trades ── */}
        {whaleError && whaleTrades.length === 0 && (
          <div className="detail-section">
            <h3 className="detail-section-title">Insider Trades</h3>
            <p className="detail-fetch-error">Daten nicht verfügbar</p>
          </div>
        )}
        {whaleTrades.length > 0 && (
          <div className="detail-section">
            <h3 className="detail-section-title">Insider Trades</h3>
            <div className="wt-insider-list">
              {whaleTrades.slice(0, 5).map((t, i) => {
                const isBuy = t.type === 'Kauf';
                const abs = Math.abs(t.value);
                const valStr = abs >= 1e9 ? `${(t.value/1e9).toFixed(1)} Mrd $`
                             : abs >= 1e6 ? `${(t.value/1e6).toFixed(1)} Mio $`
                             : `${Math.round(t.value/1e3)} K $`;
                return (
                  <div key={t.id || i} className={`wt-insider-card ${isBuy ? 'wt-insider--buy' : 'wt-insider--sell'}`}>
                    <WtLogo ticker={t.ticker} />
                    <div className="wt-insider-mid">
                      <span className="wt-insider-name">{t.insiderName}</span>
                      <span className="wt-insider-sub">
                        <span className={isBuy ? 'wt-insider-arrow buy' : 'wt-insider-arrow sell'}>{isBuy ? '▲' : '▼'}</span>
                        {' '}{t.ticker}{t.role ? ` · ${t.role}` : ''}
                      </span>
                    </div>
                    <div className="wt-insider-right">
                      <span className={`wt-insider-val ${isBuy ? 'buy' : 'sell'}`}>{isBuy ? '+' : '−'}{valStr}</span>
                      <span className="wt-insider-date">{t.transactionDate ? new Date(t.transactionDate).toLocaleDateString('de-DE', { day: 'numeric', month: 'short' }) : ''}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Top Fonds 13F ── */}
        {fundError && fundMoves.length === 0 && (
          <div className="detail-section">
            <h3 className="detail-section-title">Top Fonds · 13F</h3>
            <p className="detail-fetch-error">Daten nicht verfügbar</p>
          </div>
        )}
        {fundMoves.length > 0 && (
          <div className="detail-section">
            <h3 className="detail-section-title">Top Fonds · 13F</h3>
            <div className="wt-insider-list">
              {fundMoves.map((m, i) => {
                const isPos = m.action === 'new' || m.action === 'increased';
                const actionLabel = { new: '★ Neu', increased: '▲ Aufgestockt', reduced: '▼ Reduziert', sold: '✕ Verkauft' }[m.action] || m.action;
                const valStr = m.currentValueM >= 1000
                  ? `${(m.currentValueM / 1000).toFixed(1)} Mrd $`
                  : m.currentValueM > 0 ? `${Math.round(m.currentValueM)} Mio $` : null;
                return (
                  <div key={i} className={`wt-insider-card ${isPos ? 'wt-insider--buy' : 'wt-insider--sell'}`}>
                    <div className="wt-fund-avatar">
                      {m.investor.split(' ').map(w => w[0]).join('').slice(0, 2)}
                    </div>
                    <div className="wt-insider-mid">
                      <span className="wt-insider-name">{m.investor}</span>
                      <span className="wt-insider-sub">
                        <span className={isPos ? 'wt-insider-arrow buy' : 'wt-insider-arrow sell'}>
                          {m.action === 'new' ? '★' : m.action === 'sold' ? '✕' : isPos ? '▲' : '▼'}
                        </span>
                        {' '}{actionLabel}{m.pctChange !== 100 && m.pctChange !== 0 ? ` ${m.pctChange > 0 ? '+' : ''}${m.pctChange}%` : ''}
                      </span>
                    </div>
                    <div className="wt-insider-right">
                      {valStr && <span className={`wt-insider-val ${isPos ? 'buy' : 'sell'}`}>{valStr}</span>}
                      <span className="wt-insider-date">{m.quarter}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <p className="detail-disclaimer">Kein Real-Money Trading · Nur Informationszweck</p>
      </div>
    </div>
  );
}

export default AssetDetail;
